/**
 * Componente ToastNotification
 * Componente visual customizado para exibir notificações toast na tela
 */

import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { CheckCircle, XCircle, AlertCircle, Info, X } from "lucide-react";

const ToastNotification = ({ toast, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const timerRef = useRef(null);

  const clearExitTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const exit = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose?.(toast.id);
    }, 300);
  };

  const startExitTimer = () => {
    clearExitTimer();
    timerRef.current = setTimeout(exit, toast.duration || 3000);
  };

  useEffect(() => {
    // Animação de entrada
    const enterTimer = setTimeout(() => setIsVisible(true), 10);

    // Iniciar timer
    startExitTimer();

    return () => {
      clearTimeout(enterTimer);
      clearExitTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMouseEnter = () => {
    clearExitTimer();
    if (isExiting) {
      setIsExiting(false);
      setIsVisible(true);
    }
  };

  const handleMouseLeave = () => {
    startExitTimer();
  };

  if (!toast) return null;

  const icons = {
    success: <CheckCircle className="w-5 h-5 text-green-600" />,
    error: <XCircle className="w-5 h-5 text-red-600" />,
    warning: <AlertCircle className="w-5 h-5 text-yellow-600" />,
    info: <Info className="w-5 h-5 text-blue-600" />,
  };

  const bgColors = {
    success: "bg-green-50 border-green-200",
    error: "bg-red-50 border-red-200",
    warning: "bg-yellow-50 border-yellow-200",
    info: "bg-blue-50 border-blue-200",
  };

  const textColors = {
    success: "text-green-800",
    error: "text-red-800",
    warning: "text-yellow-800",
    info: "text-blue-800",
  };

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`
        fixed top-4 left-1/2 transform -translate-x-1/2 z-[9999999]
        min-w-[300px] max-w-[500px] w-auto
        rounded-lg shadow-lg border-2 p-4
        flex items-start gap-3
        transition-all duration-300 ease-in-out
        ${bgColors[toast.type] || bgColors.info}
        ${isVisible && !isExiting ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"}
        ${isExiting ? "opacity-0 translate-y-2" : ""}
      `}
      style={{ pointerEvents: "auto" }}
    >
      <div className="flex-shrink-0 mt-0.5">
        {icons[toast.type] || icons.info}
      </div>
      <div className={`flex-1 ${textColors[toast.type] || textColors.info}`}>
        <p className="font-medium text-sm">{toast.message}</p>
      </div>
      <button
        onClick={() => exit()}
        className={`flex-shrink-0 ${textColors[toast.type] || textColors.info} hover:opacity-70 transition-opacity`}
        aria-label="Fechar"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};


export const ToastNotificationContainer = ({ toasts, onClose }) => {
  if (!toasts || toasts.length === 0) return null;

  return createPortal(
    <div
      className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[9999999] pointer-events-none"
      style={{
        position: 'fixed',
        top: '16px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999999,
        pointerEvents: 'none'
      }}
    >
      <div className="flex flex-col gap-2 items-center">
        {toasts.slice(-5).map((toast, index) => (
          <div
            key={toast.id}
            style={{
              marginTop: `${index * 4}px`,
            }}
          >
            <ToastNotification toast={toast} onClose={onClose} />
          </div>
        ))}
      </div>
    </div>,
    document.body
  );
};

export default ToastNotification;



















